import React, { useState, useEffect } from 'react';
import {
  FaLeaf, FaHome, FaMapMarkedAlt, FaBuilding, FaTree, FaStore,
  FaCheckCircle, FaInfoCircle, FaTimesCircle, FaExclamationTriangle
} from 'react-icons/fa';
import API_URL from '../utils/api';

// ─── Minimal neutral color scheme for all sections ──────────────────────────
const NEUTRAL_CS = {
  primary:    'text-orange-600',
  heading:    'text-orange-600',
  border:     'border-gray-200',
  border2:    'border-gray-100',
  bg:         'bg-white',
  bgGrad:     'from-white to-white',
  stepBg:     'bg-gray-50',
  innerPanel: 'bg-white',
  stepCircle: 'bg-orange-600',
  intro:      'bg-white border-gray-200',
  check:      'text-orange-600',
};

const COLOR_SCHEMES = {
  agricultural: NEUTRAL_CS,
  independent:  NEUTRAL_CS,
  plot:         NEUTRAL_CS,
  apartment:    NEUTRAL_CS,
  farmhouse:    NEUTRAL_CS,
  commercial:   NEUTRAL_CS,
};

const RATING_COLORS = {
  '90-100': 'bg-green-100 text-green-800 border-green-300',
  '80-100': 'bg-green-100 text-green-800 border-green-300',
  '75-90':  'bg-blue-100 text-blue-800 border-blue-300',
  '60-80':  'bg-blue-100 text-blue-800 border-blue-300',
  '60-75':  'bg-yellow-100 text-yellow-800 border-yellow-300',
  '40-60':  'bg-orange-100 text-orange-800 border-orange-300',
  '20-40':  'bg-orange-100 text-orange-800 border-orange-300',
  '0-20':   'bg-red-100 text-red-800 border-red-300',
  'Below 40':'bg-red-100 text-red-800 border-red-300',
};

// Exact hex colors for scale left-hand badges (matching the images)
const SCALE_PILL_HEX = {
  '90-100': '#28a745', // green
  '80-100': '#28a745',
  '75-90':  '#2b6cf6', // blue
  '60-80':  '#2b6cf6',
  '60-75':  '#c07a00', // gold
  '40-60':  '#c07a00',
  '20-40':  '#f97316', // orange
  '0-20':   '#dc2626', // red
  'Below 40':'#dc2626',
};

const normalizeRange = (range) => {
  if (!range) return '';
  let s = String(range).replace(/%/g, '').replace(/\u2013|\u2014/g, '-').trim();
  // compact multiple spaces
  s = s.replace(/\s+/g, ' ');
  // "Below 40" variations
  if (/below\s*\d+/i.test(s)) return 'Below 40';
  // numeric ranges like "80-100" or "80 - 100"
  const m = s.match(/(\d+)\s*-\s*(\d+)/);
  if (m) return `${m[1]}-${m[2]}`;
  return s;
};

const isHtmlString = (value) => {
  return typeof value === 'string' && /<[^>]+>/.test(value);
};

const getPillHex = (range) => {
  if (!range) return '#e5e7eb';
  const norm = normalizeRange(range);
  if (SCALE_PILL_HEX[norm]) return SCALE_PILL_HEX[norm];
  // fallback: try to find a key contained in normalized string
  const key = Object.keys(SCALE_PILL_HEX).find(k => norm.includes(k) || k.includes(norm));
  return key ? SCALE_PILL_HEX[key] : '#e5e7eb';
};

const normalizeHeadingLabel = (text) => {
  if (!text) return text;
  return text
    .replace(/^[^A-Za-z0-9]+/, '')
    .replace(/^[0-9️⃣\s.-]+/, '')
    .replace(/(Comparison|Investment|Guide)$/i, '')
    .trim();
};

const normalizeComparisonCell = (value, expectedLabel) => {
  if (!value) return '';
  let cell = String(value).trim();
  const parenMatch = cell.match(/\(([^)]+)\)/);
  if (expectedLabel) {
    const normalizedExpected = normalizeHeadingLabel(expectedLabel).toLowerCase();
    const normalizedCell = cell.toLowerCase();
    if (normalizedExpected && normalizedCell.startsWith(normalizedExpected)) {
      const rest = cell.slice(normalizedExpected.length).trim();
      if (rest.startsWith('(') && rest.endsWith(')')) {
        return rest.slice(1, -1).trim();
      }
    }
  }
  if (parenMatch) {
    return parenMatch[1].trim();
  }
  return cell;
};

const CARD_COLORS = [
  { border: 'border-l-4 border-green-500', check: 'text-green-600' },
  { border: 'border-l-4 border-yellow-500', check: 'text-yellow-600' },
  { border: 'border-l-4 border-orange-500', check: 'text-orange-500' },
  { border: 'border-l-4 border-purple-500', check: 'text-purple-600' },
  { border: 'border-l-4 border-gray-400',   check: 'text-gray-500' },
  { border: 'border-l-4 border-blue-500',   check: 'text-blue-600' },
];

const getCardColor = (title, idx) => {
  const t = title.toLowerCase();
  if (t.includes('wet') || t.includes('irrigated')) return CARD_COLORS[0];
  if (t.includes('dry') || t.includes('rain'))      return CARD_COLORS[1];
  if (t.includes('garden') || t.includes('horticulture')) return CARD_COLORS[2];
  if (t.includes('plantation'))                      return CARD_COLORS[3];
  if (t.includes('fallow'))                          return CARD_COLORS[4];
  if (t.includes('livestock') || t.includes('agro')) return CARD_COLORS[5];
  return CARD_COLORS[idx % CARD_COLORS.length];
};

const getRatingClass = (range) => {
  const key = Object.keys(RATING_COLORS).find(k => range?.includes(k));
  return key ? RATING_COLORS[key] : 'bg-gray-100 text-gray-800 border-gray-300';
};

// ─── Content renderer ─────────────────────────────────────────────────────────
const renderContent = (text, cs) => {
  if (!text) return null;

  const raw = text.split('\n\n');
  const output = [];
  let cardBuf = [];
  let pendingTitle = null;
  let pendingCardColor = null;
  let cardColorIdx = 0;

  const flushCards = (key) => {
    if (!cardBuf.length) return;
    output.push(
      <div key={`cards-${key}`} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        {cardBuf.map((c, i) => (
          <div key={i} className={`bg-white ${c.color.border} rounded p-4`}>
            <div className="flex items-center gap-2 mb-2">
              <FaCheckCircle className={`${c.color.check} flex-shrink-0`} />
              <h4 className="font-semibold text-gray-800">{c.title}</h4>
            </div>
            {c.subtext && <p className="text-gray-600 text-sm mb-2">{c.subtext}</p>}
            <ul className="space-y-1">
              {c.items.map((it, j) => (
                <li key={j} className="text-sm text-gray-700">• {it.replace(/^•\s*/, '')}</li>
              ))}
            </ul>
            {c.footer && <p className="text-xs text-gray-500 italic mt-2">{c.footer}</p>}
          </div>
        ))}
      </div>
    );
    cardBuf = [];
  };

  raw.forEach((sec, idx) => {
    const s = sec.trim();
    if (!s) return;

    // ── ✅ card title ──────────────────────────────────────────
    if (s.startsWith('✅')) {
      if (pendingTitle) {
        cardBuf.push({ title: pendingTitle, items: [], subtext: '', footer: '', color: pendingCardColor });
      }
      const lines = s.split('\n');
      const title = lines[0].replace(/^✅\s*/, '').trim();
      const bullets = lines.slice(1).filter(l => l.trim().startsWith('•'));
      const subs = lines.slice(1).filter(l => l.trim() && !l.trim().startsWith('•'));
      const color = getCardColor(title, cardColorIdx++);
      pendingTitle = title;
      pendingCardColor = color;
      if (bullets.length) {
        const lastSub = subs[subs.length - 1] || '';
        const isFooter = /^(Ideal|Provides|Commercial|Used)/i.test(lastSub);
        cardBuf.push({ title, items: bullets, subtext: subs.filter(l => l !== lastSub || !isFooter).join(' ').trim(), footer: isFooter ? lastSub : '', color });
        pendingTitle = null; pendingCardColor = null;
      }
      return;
    }

    // ── bullet block attached to pending ✅ title ──────────────
    if (pendingTitle && s.includes('•')) {
      const lines = s.split('\n');
      const items = lines.filter(l => l.trim().startsWith('•'));
      const subs = lines.filter(l => l.trim() && !l.trim().startsWith('•'));
      const lastSub = subs[subs.length - 1] || '';
      const isFooter = /^(Ideal|Provides|Commercial|Used)/i.test(lastSub);
      cardBuf.push({ title: pendingTitle, items, subtext: subs.filter(l => l !== lastSub || !isFooter).join(' ').trim(), footer: isFooter ? lastSub : '', color: pendingCardColor });
      pendingTitle = null; pendingCardColor = null;
      return;
    }

    // flush orphan pending
    if (pendingTitle) {
      cardBuf.push({ title: pendingTitle, items: [], subtext: '', footer: '', color: pendingCardColor || CARD_COLORS[0] });
      pendingTitle = null; pendingCardColor = null;
    }
    flushCards(idx);

    // ── STEP box ───────────────────────────────────────────────
    if (s.startsWith('STEP ')) {
      const firstLine = s.split('\n')[0];
      const m = firstLine.match(/^STEP (\d+) — (.+)$/);
      if (m) {
        const restLines = s.split('\n').slice(1);
        const bullets = restLines.filter(l => l.trim().startsWith('•'));
        const extras  = restLines.filter(l => l.trim() && !l.trim().startsWith('•'));

        // prepare inner lines for two-column or warnings
        const innerLines = restLines.join('\n').split('\n').map(l => l.trim()).filter(Boolean);
        const hasArrows = innerLines.some(l => l.includes('→') || l.includes('->'));

        output.push(
          <div key={idx} className="mb-6 border rounded-md overflow-hidden">
          <div className="bg-gray-100 px-4 py-3 flex items-center gap-3">
            <div className={`w-8 h-8 rounded-full ${cs.stepCircle} text-white flex items-center justify-center text-sm font-semibold`}>{m[1]}</div>
            <h4 className="text-lg font-semibold text-gray-800">{m[2]}</h4>
          </div>

            <div className="p-4 bg-white border-t border-gray-100 text-gray-800">
              {hasArrows ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {innerLines.map((line, i) => {
                    const parts = line.split(/→|->/).map(p => p.trim());
                    return (
                      <React.Fragment key={i}>
                        <div className="text-sm font-medium">{parts[0]}</div>
                        <div className="text-sm text-gray-700">{parts[1] || ''}</div>
                      </React.Fragment>
                    );
                  })}
                </div>
              ) : (
                <>
                  {extras.map((e, i) => <p key={i} className="text-sm text-gray-700 mb-1">{e}</p>)}
                  {bullets.length > 0 && (
                    <ul className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 list-disc pl-5 text-gray-700">
                      {bullets.map((b, i) => (
                        <li key={i} className="text-sm">{b.replace(/^•\s*/, '')}</li>
                      ))}
                    </ul>
                  )}
                </>
              )}

              {innerLines.map((l, i) => (l.toLowerCase().includes('if any check fails') || l.toLowerCase().includes('clarify before')) ? (
                <p key={`warn-${i}`} className="text-red-600 font-semibold mt-3">{l}</p>
              ) : null)}
            </div>
          </div>
        );
        return;
      }
    }

    // ── Section heading **…** ──────────────────────────────────
      if (s.startsWith('**') && s.endsWith('**')) {
        const heading = s.replace(/\*\*/g, '');
        // choose an icon based on heading keywords
        const chooseIcon = (text) => {
          const t = text.toLowerCase();
          if (t.includes('residential')) return FaHome;
          if (t.includes('agricultural') || t.includes('agriculture')) return FaLeaf;
          if (t.includes('plot') || t.includes('open plot')) return FaMapMarkedAlt;
          if (t.includes('apartment') || t.includes('flat')) return FaBuilding;
          if (t.includes('farmhouse')) return FaTree;
          if (t.includes('commercial') || t.includes('office')) return FaStore;
          return FaInfoCircle;
        };
        const HeadingIcon = chooseIcon(heading);
        output.push(
          <div key={idx} className="mb-8 mt-10">
            <div className={`flex items-center gap-3 mb-4 pb-3 border-b-2 ${cs.border2}`}>
              <HeadingIcon className={`text-3xl ${cs.heading}`} />
              <h3 className="text-2xl font-bold text-gray-800">{heading}</h3>
            </div>
          </div>
        );
        return;
      }

    // ── Rating / Conversion / Risk Scale ──────────────────────
    if (s.includes('Rating Scale:') || s.includes('Conversion Scale') || s.includes('Risk Scale:')) {
      const lines = s.split('\n');
      const titleLine = lines[0].trim();
      const ratingLines = lines.slice(1).filter(l => l.trim());
      output.push(
        <div key={idx} className="mb-6 rounded-md overflow-hidden">
          <div className="px-4 py-2">
            <div className="text-lg font-semibold text-gray-800">{titleLine.replace('Rating Scale:', '').replace('Conversion Scale', 'Conversion Scale').replace('Risk Scale:', '').trim() || 'Scale'}</div>
          </div>
          <div className="p-4 bg-white border-t border-gray-100">
            <div className="space-y-3">
              {ratingLines.map((line, i) => {
                const [rangeRaw, labelRaw] = line.split(':').map(s => s && s.trim());
                const range = rangeRaw || ''; const label = labelRaw || '';
                if (!range) return null;
                const pillHex = getPillHex(range);
                return (
                  <div key={i} className="flex items-center gap-4">
                    <div style={{ backgroundColor: pillHex, color: '#fff' }} className={`font-semibold px-4 py-2 rounded-md w-36 text-center`}>{range.replace(/^•\\s*/, '')}</div>
                    <div className="text-gray-800">{label}</div>
                  </div>
                );
              })}
            </div>
          </div>
          {ratingLines.length > 0 && <div className="px-4 py-2 text-sm text-gray-500 italic">{/* footer kept empty unless provided separately */}</div>}
        </div>
      );
      return;
    }

    // ── Comparison table supported for lines like "Feature: Option A vs Option B" ─────────────────
    const allLines = s.split('\n').map(l => l.trim()).filter(l => l);
    const headingLine = allLines.find(l => /^\*\*.*\*\*$/.test(l) && /\bvs\b/i.test(l));
    const comparisonLines = allLines.filter(l => l.includes(':') && /\bvs\b/i.test(l));
    if (comparisonLines.length >= 2) {
      const rows = [];
      let leftLabel = 'Option A';
      let rightLabel = 'Option B';
      let displayHeading = '';

      if (headingLine) {
        const headingText = headingLine.replace(/\*\*/g, '').trim();
        displayHeading = headingText;
        const normalizedHeading = normalizeHeadingLabel(headingText);
        const headingParts = normalizedHeading.split(/\s+vs\s+/i).map(p => p.trim());
        if (headingParts.length === 2) {
          leftLabel = headingParts[0];
          rightLabel = headingParts[1];
        }
      }

      comparisonLines.forEach((line, i) => {
        const parts = line.split(':');
        const feature = parts.shift().trim();
        const values = parts.join(':').trim().split(/\s+vs\s+/i).map(v => v.trim());
        if (values.length === 2) {
          const leftRaw = values[0];
          const rightRaw = values[1];
          if (i === 0 && !headingLine) {
            leftLabel = leftRaw.replace(/\s*\(.*\)$/, '').trim() || leftLabel;
            rightLabel = rightRaw.replace(/\s*\(.*\)$/, '').trim() || rightLabel;
          }
          rows.push([feature, normalizeComparisonCell(leftRaw, leftLabel), normalizeComparisonCell(rightRaw, rightLabel)]);
        }
      });

      if (rows.length >= 2) {
        return output.push(
          <div key={idx} className="mb-6">
            {displayHeading && <div className="mb-4"><h4 className="text-xl font-semibold text-gray-800">{displayHeading}</h4></div>}
            <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
              <table className="min-w-full table-auto border-collapse text-sm">
                <thead>
                  <tr className="bg-gray-100 border-b border-gray-200">
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-800">Feature</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-800">{leftLabel}</th>
                    <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-800">{rightLabel}</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {rows.map((row, i) => (
                    <tr key={i}>
                      <td className="border border-gray-200 px-4 py-3 text-gray-700 font-medium">{row[0]}</td>
                      <td className="border border-gray-200 px-4 py-3 text-gray-700">{row[1]}</td>
                      <td className="border border-gray-200 px-4 py-3 text-gray-700">{row[2]}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      }
    }

    // ── Table (Markdown or dash-separated fallback) ─────────────────
    if (s.includes('|') && s.split('\n').filter(l => l.includes('|')).length > 2) {
      const allLines = s.split('\n');
      const tableLines = allLines.filter(l => l.trim() && l.includes('|'));
      const footerLines = allLines.filter(l => l.trim() && !l.includes('|'));
      const headers = tableLines[0].split('|').map(h => h.trim()).filter(Boolean);
      const rows = tableLines.slice(2).map(r => r.split('|').map(c => c.trim()).filter(Boolean));
      output.push(
        <div key={idx} className="mb-6">
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full table-auto border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200">
                  {headers.map((h, i) => <th key={i} className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-800">{h}</th>)}
                </tr>
              </thead>
              <tbody className="bg-white">
                {rows.map((row, i) => (
                  <tr key={i}>
                    {row.map((cell, j) => (
                      <td key={j} className={`border border-gray-200 px-4 py-3 text-gray-700 ${j === 0 ? 'font-medium' : ''}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {footerLines.map((l, i) => <p key={i} className="text-gray-500 text-sm italic mt-2">{l}</p>)}
        </div>
      );
      return;
    }

    // Fallback: detect dash/em-dash separated rows (e.g. "R1 Zone — Urban areas...")
    const dashLines = s.split('\n').map(l => l.trim()).filter(l => l);
    const dashCount = dashLines.filter(l => l.includes('—') || l.includes(' - ')).length;
    if (dashCount >= 2) {
      const rows = dashLines.filter(l => l.includes('—') || l.includes(' - ')).map(l => {
        const parts = l.split(/—| - /).map(p => p.trim()).filter(Boolean);
        if (parts.length === 1) return [parts[0], ''];
        return [parts[0], parts.slice(1).join(' — ')];
      });
      const footerLines = dashLines.filter(l => !(l.includes('—') || l.includes(' - ')));
      output.push(
        <div key={idx} className="mb-6">
          <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
            <table className="min-w-full table-auto border-collapse text-sm">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-200">
                  <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-800">Zone</th>
                  <th className="border border-gray-200 px-4 py-3 text-left font-semibold text-gray-800">Description</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {rows.map((row, i) => (
                  <tr key={i}>
                    <td className="border border-gray-200 px-4 py-3 text-gray-700 font-medium">{row[0]}</td>
                    <td className="border border-gray-200 px-4 py-3 text-gray-700">{row[1]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {footerLines.map((l, i) => <p key={i} className="text-gray-500 text-sm italic mt-2">{l}</p>)}
        </div>
      );
      return;
    }

    // ── Bullet list ───────────────────────────────────────────
    if (s.includes('•')) {
      const lines = s.split('\n');
      const items = lines.filter(l => l.trim().startsWith('•'));
      const heading = lines.filter(l => l.trim() && !l.trim().startsWith('•')).join('\n').trim();
      output.push(
        <div key={idx} className="mb-6">
          {heading && <p className="text-gray-700 leading-relaxed mb-3 font-medium">{heading}</p>}
          <div className={`p-4 rounded border ${cs.border2} bg-white`}>
            <ul className="space-y-2">
              {items.map((item, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-700">
                  <FaCheckCircle className={`${cs.check} mt-1 flex-shrink-0`} />
                  <span className="leading-relaxed">{item.replace(/^•\s*/, '')}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      );
      return;
    }

    // ── checkmark list ──────────────────────────────────────
    if (s.includes('✓') || s.includes('✅')) {
      const lines = s.split('\n');
      const items = lines.filter(l => l.trim().startsWith('✓') || l.trim().startsWith('✅'));
      const heading = lines.filter(l => l.trim() && !l.trim().startsWith('✓') && !l.trim().startsWith('✅')).join(' ').trim();
      output.push(
        <div key={idx} className="mb-5">
          {heading && <p className="font-semibold text-gray-900 mb-3">{heading}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-2 text-gray-700 text-sm">
                <FaCheckCircle className={`${cs.check} flex-shrink-0`} />
                {it.replace(/^✓\s*|^✅\s*/g, '')}
              </div>
            ))}
          </div>
        </div>
      );
      return;
    }

    // ── Plain paragraph ────────────────────────────────────────
    output.push(<p key={idx} className="text-gray-700 leading-relaxed mb-4 text-base">{s}</p>);
  });

  if (pendingTitle) cardBuf.push({ title: pendingTitle, items: [], subtext: '', footer: '', color: pendingCardColor || CARD_COLORS[0] });
  flushCards('end');
  return output;
};

// ─── Section Guide ────────────────────────────────────────────────────────────
const SectionGuide = ({ sectionId, icon: Icon, title, intro, mainContent }) => {
  const cs = COLOR_SCHEMES[sectionId] || COLOR_SCHEMES.agricultural;
  return (
    <div className="prose max-w-none rounded-3xl border border-gray-200 bg-white shadow-sm p-8">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-8">
        <div className="flex items-center gap-3">
          <Icon className={`text-3xl ${cs.primary}`} />
          <div>
            <div className={`inline-flex items-center gap-2 rounded-full bg-orange-100 px-3 py-1 text-sm font-semibold text-orange-700`}>Guide Section</div>
            <h2 className={`text-3xl font-bold ${cs.primary} mt-2`}>{title}</h2>
          </div>
        </div>
      </div>

      {intro && (
        <div className="rounded-3xl border border-orange-200 bg-orange-50 p-6 mb-10">
          <p className="text-gray-800 leading-relaxed whitespace-pre-line">{intro}</p>
        </div>
      )}

      <div className="space-y-8">
        {mainContent ? (
          isHtmlString(mainContent) ? (
            <div className="prose max-w-none" dangerouslySetInnerHTML={{ __html: mainContent }} />
          ) : (
            renderContent(mainContent, cs)
          )
        ) : (
          <div className="rounded-3xl border border-dashed border-gray-200 bg-gray-50 p-6 text-center text-gray-600">
            Guide content will appear here once it is available.
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Guide Page ──────────────────────────────────────────────────────────
const Guide = () => {
  const [activeSection, setActiveSection] = useState('agricultural');
  const [cms, setCms] = useState({});

  useEffect(() => {
    fetch(`${API_URL}/api/cms/guide`)
      .then(r => r.json())
      .then(blocks => {
        if (Array.isArray(blocks)) {
          const map = {};
          blocks.forEach(b => {
            const c = b.content || {};
            // Normalize: GuidePageEditor saves {intro, fullContent}
            // GuideContentEditor saves {introduction, customContent}
            // Support both so either admin tool works
            map[b.section] = {
              ...c,
              intro: c.intro || c.introduction || '',
              fullContent: c.fullContent || c.customContent || '',
            };
          });
          setCms(map);
        }
      })
      .catch(() => {});
  }, []);

  const sections = [
    { id: 'agricultural', name: 'Agricultural Land',  icon: FaLeaf,         title: 'USER GUIDE — AGRICULTURAL LAND' },
    { id: 'independent',  name: 'Independent House',  icon: FaHome,         title: 'COMPLETE USER GUIDE — INDEPENDENT HOUSE & VILLA' },
    { id: 'plot',         name: 'Open Plot',           icon: FaMapMarkedAlt, title: 'COMPLETE USER GUIDE — OPEN PLOT (LAND INVESTMENT)' },
    { id: 'apartment',    name: 'Apartment',           icon: FaBuilding,     title: 'COMPLETE USER GUIDE — APARTMENT / FLAT' },
    { id: 'farmhouse',    name: 'Farmhouse',           icon: FaTree,         title: 'COMPLETE USER GUIDE — FARMHOUSE PROPERTY' },
    { id: 'commercial',   name: 'Commercial',          icon: FaStore,        title: 'COMPLETE USER GUIDE — OFFICE / COMMERCIAL SPACE' },
  ];

  const active = sections.find(s => s.id === activeSection);

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <FaLeaf className="text-4xl text-orange-600" />
            <h1 className="text-3xl font-bold text-gray-900">{cms.header?.title || 'TANAVI GUIDE'}</h1>
          </div>
          <p className="text-xl text-gray-600">
            {cms.header?.subtitle || 'Professional • Structured • Easy for Any User to Understand'}
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-8">
          {sections.map(({ id, name, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveSection(id)}
              className={`flex items-center gap-2.5 px-5 py-3 rounded-2xl font-semibold text-sm transition-all duration-200 border-2 shadow-md hover:scale-105 active:scale-95 ${
                  activeSection === id
                    ? 'bg-orange-600 text-white border-orange-500 shadow-orange-200 shadow-lg'
                    : 'bg-white text-gray-700 hover:bg-orange-50 hover:text-orange-600 hover:border-orange-400 border-gray-300 hover:shadow-orange-100 hover:shadow-lg'
                }`}
            >
              <Icon className="text-base text-current flex-shrink-0" />
              {name}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-white rounded-lg shadow-lg p-8">
          <SectionGuide
            sectionId={activeSection}
            icon={active.icon}
            title={active.title}
            intro={cms[activeSection]?.intro}
            mainContent={cms[activeSection]?.fullContent}
          />
        </div>

      </div>
    </div>
  );
};

export default Guide;

